import { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { AuthContext } from '../context/AuthContext.jsx';
import { useCompany } from '../context/CompanyContext.jsx';
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import './Layout.css';

/**
 * Main Layout component with header, sidebar, and content area
 */
export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { selectedCompany, companyList, updateSelectedCompany, loading } = useCompany();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [pathname]);

  const menuItems = [
    { path: '/', label: 'Dashboard' },
    { path: '/vouchers/list', label: 'Outstanding Bills' },
    { path: '/receipts', label: 'Add Receipts' },
    { path: '/receipts/list', label: 'Receipt List' },
    ...(user?.isOwner
      ? [
          { path: '/receipts/summary', label: 'Collection Summary' },
          { path: '/receipts/report', label: 'User-wise Collections' },
        ]
      : []),
    { path: '/customer-ledger', label: 'Ledger Outstanding' },
    { path: '/stock-register', label: 'Stock Register' },
    { path: '/rent-register', label: 'Rent Register' },
    { path: '/party-wise-statement', label: 'Party Wise Statement' },
    { path: '/gatepass', label: 'Gatepass Entry' },
    { path: '/gatepass/list', label: 'Gatepass List' },
  ];

  const userDisplayName = user?.userName || 'User';
  const userDisplayId = user?.tallyUserId || '';

  const handleCompanyChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      updateSelectedCompany(selectedOption);
    }
  };

  return (
    <div className="layout">
      {/* Header */}
      <header className="layout-header">
        <button
          className="layout-menu-toggle"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
          aria-expanded={sidebarOpen}
        >
          <span className="layout-menu-icon">☰</span>
        </button>

        <div className="layout-logo">
          <span className="layout-logo-text">Setu Link</span>
        </div>

        {/* Company Dropdown - Global selector */}
        {/* Show when companies are available (userWise must be enabled for companies to be fetched) */}
        {companyList && Array.isArray(companyList) && companyList.length > 0 && (
          <div className="layout-company-selector">
            <Select
              options={companyList.map(c => ({ 
                value: c.companyId, 
                label: `${c.companyName} (${c.companyId})` 
              }))}
              value={selectedCompany ? { value: selectedCompany.value, label: selectedCompany.label } : null}
              onChange={handleCompanyChange}
              placeholder="Select Company"
              isClearable={false}
              isSearchable={false}
              className="layout-company-select"
              classNamePrefix="react-select"
              isDisabled={loading}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                }),
                singleValue: (base) => ({
                  ...base,
                  color: '#ffffff',
                }),
                input: (base) => ({
                  ...base,
                  color: '#ffffff',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: 'rgba(255, 255, 255, 0.7)',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  position: 'fixed',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                  position: 'fixed',
                }),
              }}
            />
          </div>
        )}

        <div className="layout-profile" ref={dropdownRef}>
          <button
            className="layout-profile-chip"
            onClick={toggleDropdown}
            aria-label="User menu"
            aria-expanded={dropdownOpen}
          >
            <span className="layout-profile-avatar" aria-hidden="true">👤</span>
            <span className="layout-profile-name">
              {userDisplayId && `${userDisplayId} - `}
              {userDisplayName}
            </span>
          </button>
          {dropdownOpen && (
            <div className="layout-dropdown">
              <Link
                to="/profile"
                onClick={closeDropdown}
                className="layout-dropdown-item"
              >
                Profile
              </Link>
              <Link
                to="/change-password"
                onClick={closeDropdown}
                className="layout-dropdown-item"
              >
                Change Password
              </Link>
              <button
                onClick={handleLogout}
                className="layout-dropdown-item layout-dropdown-item-button"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`layout-sidebar ${sidebarOpen ? 'layout-sidebar-open' : ''}`}>
        <nav className="layout-nav">
          {menuItems.map((item) => {
            const isActive = pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`}
                onClick={closeSidebar}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="layout-backdrop"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Content */}
      <main className="layout-content">{children}</main>
    </div>
  );
}
